// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Fetches an equipment's observed telemetry for the past 24h and maps it into
// per-metric chart series. Fails gracefully: any missing id, empty result, or
// request error resolves to an empty series list so the caller renders nothing.

import { useEffect, useState } from 'react';
import { getEquipmentObservations, toMetricSeries } from '../services/telemetryService';
import type { MetricSeries } from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Load the last 24h of observations for `equipmentId` at `siteId`, scoped to
 * `orgId`. Returns `{ series, loading }`; `series` is `[]` until data arrives and
 * stays `[]` when there is nothing to show (missing ids, no points, or an error).
 */
export function useEquipmentTelemetry(
  siteId?: string,
  equipmentId?: string,
  orgId?: string
): { series: MetricSeries[]; loading: boolean } {
  const [series, setSeries] = useState<MetricSeries[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!siteId || !equipmentId || !orgId) {
      setSeries([]);
      setLoading(false);
      return;
    }

    const run = async () => {
      setLoading(true);
      const end = new Date();
      const start = new Date(end.getTime() - DAY_MS);
      try {
        const envelope = await getEquipmentObservations(siteId, equipmentId, {
          orgId,
          start: start.toISOString(),
          end: end.toISOString(),
          resolution: 'PT1H',
        });
        if (!cancelled) setSeries(toMetricSeries(envelope.data));
      } catch {
        // No telemetry for this equipment (404/empty/network) — show nothing.
        if (!cancelled) setSeries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [siteId, equipmentId, orgId]);

  return { series, loading };
}
